import { apiRequest, createAntdFilter, VojomoLoader } from "@vojomo-core/shared";
import BaseLayout from "../layouts/BaseLayout";
import { useCallback, useEffect, useState } from "react";
import { Table, Checkbox, Tooltip, Tag } from "antd";
import { ColumnsType } from "antd/es/table";

interface AdvisorUser {
  _id: string;
  name: string;
  email: string;
  company: string;
  is_advisor: boolean;
}

export const Advisors = () => {
  const [users, setUsers] = useState<AdvisorUser[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    setLoading(true);
    const data = await apiRequest<AdvisorUser[]>({
      subRoute: "admin",
      path: "/advisors",
      method: "GET",
    });
    if (Array.isArray(data)) setUsers(data);
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleAdvisorToggle = async (userId: string, isAdvisor: boolean) => {
    // Optimistic update
    setUsers((prev) =>
      prev.map((u) => (u._id === userId ? { ...u, is_advisor: isAdvisor } : u))
    );

    const response = await apiRequest<{ success: boolean; message: string }>({
      subRoute: "admin",
      path: "/update_advisor",
      method: "PATCH",
      body: { user_id: userId, is_advisor: isAdvisor },
    });

    if (!response.success) {
      // Revert on failure
      fetchData();
    }
  };

  const advisorCount = users.filter((u) => u.is_advisor).length;

  const columns: ColumnsType<AdvisorUser> = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      ...createAntdFilter("name", "Name"),
      sorter: (a, b) => a.name.localeCompare(b.name),
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      ...createAntdFilter("email", "Email"),
    },
    {
      title: "Company",
      dataIndex: "company",
      key: "company",
      ...createAntdFilter("company", "Company"),
      sorter: (a, b) => a.company.localeCompare(b.company),
    },
    {
      title: "Global Advisor",
      dataIndex: "is_advisor",
      key: "is_advisor",
      width: 140,
      filters: [
        { text: "Advisor", value: true },
        { text: "Not advisor", value: false },
      ],
      onFilter: (value, record) => record.is_advisor === value,
      render: (_: unknown, record: AdvisorUser) => (
        <Tooltip title="Global Advisors have full Advisor rights on every session without needing a per-session designation.">
          <Checkbox
            checked={record.is_advisor}
            onChange={(e) => handleAdvisorToggle(record._id, e.target.checked)}
          />
        </Tooltip>
      ),
    },
  ];

  return (
    <BaseLayout pageTitle="Advisors">
      <div style={{ marginTop: 24 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 8 }}>
          <h2 style={{ margin: 0 }}>Global Advisors</h2>
          {!loading && (
            <Tag color="purple">{advisorCount} advisor{advisorCount !== 1 ? "s" : ""}</Tag>
          )}
        </div>
        <p style={{ color: "#64748b", marginBottom: 20, fontSize: 14 }}>
          Users with the Global Advisor flag have full Advisor rights on every roadmap session on
          the platform — no per-session designation required. Currently only Vojomo personnel
          should hold this designation.
        </p>

        {loading ? (
          <VojomoLoader />
        ) : (
          <Table
            columns={columns}
            dataSource={users}
            rowKey="_id"
            scroll={{ y: "65vh" }}
            pagination={{ pageSize: 50, showSizeChanger: false }}
            rowClassName={(record) =>
              record.is_advisor ? "ant-table-row-selected" : ""
            }
          />
        )}
      </div>
    </BaseLayout>
  );
};
